import { useMemo, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@jammable/ui-components/Popover/Popover";
import { Input } from "@jammable/ui-components/Forms";
import millify from "millify";
import VoiceImage from "./VoiceImage";
import dayjs from "dayjs";
import { requireIdToken } from "../utils/firebaseAuth";
import axios from "axios";
import { useQuery } from "react-query";
import { Spinner } from "@jammable/ui-components/Loader";
import NotFound from "./NotFound";
import { X, ChevronDown } from "lucide-react";
import { FaTrash } from "react-icons/fa";

const NewerVersionSelect = ({ setNewerVersion, newerVersion }) => {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  const formatRelativeTime = (date) => {
    const duration = dayjs().diff(dayjs(date), "minute");

    if (duration < 1) return "just now";
    if (duration < 60) return `${duration}m`;
    const hours = Math.floor(duration / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const [search, setSearch] = useState("");

  const { data: userVoices, isLoading } = useQuery(
    ["userVoices", search],
    async () => {
      const token = await requireIdToken();
      const response = await axios.get("/api/user/getMyVoices", {
        params: {
          type: "all",
          page: 1,
          limit: 100,
          search: search,
        },
        headers: { Authorization: token },
      });

      return response.data.models;
    },
    { enabled: open },
  );

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-font-semibold">Newer Version (Optional)</div>
      <div className="tw-text-sm tw-text-gray-500">
        Select a newer version of this voice to inform users.
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="tw-w-full tw-cursor-pointer tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-all tw-items-center tw-relative tw-text-left tw-flex tw-justify-between tw-border tw-border-gray-300 tw-p-3 tw-rounded-xl">
            <div className="tw-flex tw-items-center tw-gap-2">
              {newerVersion ? (
                <>
                  <VoiceImage
                    model={newerVersion}
                    size={35}
                    className="tw-rounded-full"
                  />
                  <div className="tw-flex tw-flex-col tw-font-semibold">
                    <span>{newerVersion?.artistname}</span>

                    <span className="tw-text-xs tw-text-black/50 tw-font-semibold">
                      {`${millify(newerVersion?.uses)} uses · Created ${formatRelativeTime(
                        newerVersion?.createdAt,
                      )}`}
                    </span>
                  </div>
                </>
              ) : (
                <span className="tw-text-black/50">No voice selected...</span>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              {newerVersion && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewerVersion(null);
                  }}
                  className="tw-cursor-pointer tw-size-8 tw-bg-red-500/20 tw-rounded-lg tw-p-2 tw-flex tw-items-center tw-justify-center"
                >
                  <FaTrash
                    className="tw-cursor-pointer tw-text-red-500"
                    size={12}
                  />
                </div>
              )}
              <div className="tw-cursor-pointer tw-size-8  tw-bg-gray-300/20 tw-rounded-lg tw-p-2 tw-flex tw-items-center tw-justify-center">
                <ChevronDown strokeWidth={5} size={15} />
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="tw-w-screen md:tw-w-full md:tw-w-[35rem] tw-p-3 tw-bg-neutral-50 tw-shadow-lg tw-rounded-3xl tw-max-h-[500px]">
          <Input
            placeholder="Search your public voices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="tw-mb-2"
          />
          <div className="tw-max-h-60 tw-overflow-y-auto">
            {isLoading ? (
              <div className="tw-flex tw-justify-center tw-items-center tw-py-4">
                <Spinner size={4} />
              </div>
            ) : !userVoices?.length ? (
              <NotFound
                title="No voices found..."
                description="Try searching for a different voice!"
                animateIn={false}
                className="tw-my-4 tw-text-lg"
              />
            ) : (
              userVoices?.map((voice) => (
                <div
                  key={voice._id}
                  onClick={() => {
                    setNewerVersion(voice);
                    setOpen(false);
                  }}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-p-2 hover:tw-bg-gray-100 tw-transition-all tw-rounded-xl"
                >
                  <VoiceImage
                    model={voice}
                    size={40}
                    className="tw-rounded-full"
                  />
                  <div className="tw-flex tw-flex-col">
                    <div className="tw-font-bold">{voice.artistname}</div>
                    <div className="tw-text-xs tw-text-black/50 tw-font-semibold">
                      {voice.uses} uses
                      {` · Created ${formatRelativeTime(voice.createdAt)}`}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default NewerVersionSelect;
